/**
 * Money as a person reads it — the digits, and the words that say which way
 * it points.
 *
 * This is deliberately not in `src/lib/money.ts`. That module is arithmetic:
 * integer cents in, integer cents out, and it is the same code on the server
 * and in the browser precisely because it has no opinion about how anything
 * looks. This one has nothing but opinions about how things look, and it is
 * the only place in the app that turns a number into a sentence.
 *
 * **Two rules the screens depend on.**
 *
 * The digits are composed from the integer. `centsToDigits` divides and takes
 * a remainder; it never evaluates `cents / 100` and it never calls `toFixed`,
 * so the string is exactly the number that is in the `*_cents` column rather
 * than the nearest thing a float could hold to it.
 *
 * **Direction is always in words.** A balance is never rendered as a coloured
 * number and left at that: every amount on both screens is accompanied by
 * "owes you", "you owe" or "settled up", written out. Colour repeats that
 * meaning and never carries it, because a red number and a green number are
 * the same number to a red-green colourblind reader, to a screen reader, and
 * to a printout. `direction` is what the components colour *and* label from,
 * so the two cannot drift apart.
 *
 * Every `describe*` hands back the `cents` it made the digits out of as well
 * as the digits themselves. Not for arithmetic — nothing recomputes anything
 * from it — but because a number on this app's screens counts to its value
 * rather than jumping to it, and the thing that counts needs the integer the
 * string was made of. The value, the direction, the label and the digits all
 * come out of one call, so a counting number cannot end up on a different
 * figure from the sentence beside it.
 */

import { percentToBips, type UserId } from './money'

/** Which way an amount points, from the point of view of the person reading. */
export type Direction = 'incoming' | 'outgoing' | 'settled'

/**
 * The minor unit is two digits everywhere, because every amount in this app is
 * stored in a column named `*_cents` and there is no conversion anywhere in
 * the codebase. A currency with a different minor unit would need the column
 * to mean something else first; this is not the place that decision gets made
 * by accident.
 */
const MINOR_UNIT_DIGITS = 2
const MINOR_UNIT = 100

/**
 * A symbol for the codes people in this app actually use, and the code itself
 * for everything else. A wrong symbol is worse than no symbol — `$` in front
 * of a number that is not dollars is a lie, where `NZD 12.00` is merely
 * plain — so anything not on the list is written out.
 */
const SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
  NZD: 'NZ$',
  CHF: 'CHF ',
  SEK: 'SEK ',
  INR: '₹',
}

/** `1234567` → `12,345.67`. Integer division and a remainder; no float. */
export function centsToDigits(cents: number): string {
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(`cents must be a whole number, got ${cents}`)
  }
  const magnitude = Math.abs(cents)
  const whole = Math.trunc(magnitude / MINOR_UNIT)
  const minor = magnitude % MINOR_UNIT

  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${grouped}.${String(minor).padStart(MINOR_UNIT_DIGITS, '0')}`
}

/**
 * What goes in front of the digits, on its own — for the one place that draws
 * an amount without holding its digits: the amount pad, where the digits are
 * inside a text box a person types into and the symbol sits beside it.
 */
export function moneySymbol(currency: string): string {
  return SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `
}

/**
 * An amount, without a sign. The sign is never shown, anywhere, because the
 * words say which way it goes and `-$90.00` next to "you owe" is either
 * redundant or contradictory depending on how you read it.
 */
export function formatMoney(cents: number, currency: string): string {
  return `${moneySymbol(currency)}${centsToDigits(cents)}`
}

/**
 * What somebody typed into an amount box, as integer cents — the inverse of
 * `centsToDigits`, and the one place text becomes money in this app.
 *
 * It is done on the string. `Number('19.99') * 100` is `1998.9999999999998`,
 * and every way of rounding that back is a way of being one cent wrong on
 * some other input; taking the two digits after the point as an integer
 * cannot be. Anything with more precision than the minor unit is refused
 * rather than rounded, because silently turning 19.999 into 20.00 is a
 * different amount than the one a person typed.
 */
export function parseMoneyInput(input: string): number | null {
  const trimmed = input.trim().replace(/[\s,]/g, '')
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed)
  if (!match) return null

  const whole = Number(match[1])
  const minor = Number((match[2] ?? '').padEnd(MINOR_UNIT_DIGITS, '0'))
  const cents = whole * MINOR_UNIT + minor
  return Number.isSafeInteger(cents) ? cents : null
}

/**
 * The same, but a sign is allowed — the split editor's adjustment column, and
 * nothing else.
 *
 * An adjustment is the one signed number in this app (see `resolveSplit`), so
 * this is the one reader that accepts a `-`. It is still done on the string,
 * for the same reason `parseMoneyInput` is: `Number('-19.99') * 100` is not
 * `-1999`.
 */
export function parseSignedMoneyInput(input: string): number | null {
  const trimmed = input.trim()
  const negative = trimmed.startsWith('-')
  const magnitude = parseMoneyInput(negative || trimmed.startsWith('+') ? trimmed.slice(1) : trimmed)
  if (magnitude === null) return null
  return negative ? -magnitude : magnitude
}

/**
 * Cents back into what somebody would have typed into one of those boxes:
 * `-1250` is `-12.50`. No symbol and no thousands separators, because it goes
 * into an `<input>` that has to be re-readable by `parseSignedMoneyInput` —
 * re-opening an expense has to put back exactly the value that was saved.
 */
export function signedMoneyInput(cents: number): string {
  return `${cents < 0 ? '-' : ''}${centsToDigits(cents).replace(/,/g, '')}`
}

/**
 * A percentage box's text as basis points. `12.5` is `1250`; the whole bill is
 * `10000`.
 *
 * `percentToBips` is where the actual conversion happens — this only decides
 * what counts as a number, so that `12.5.3` is refused as text rather than
 * arriving at the arithmetic as `NaN`. Anything finer than a hundredth of a
 * percent is refused there, not rounded here.
 */
export function parsePercentInput(input: string): number | null {
  const trimmed = input.trim().replace(/%$/, '').trim()
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return null
  try {
    return percentToBips(Number(trimmed))
  } catch {
    return null
  }
}

/** `1250` back into `12.5` — the inverse, for re-opening a saved percentage split. */
export function percentInput(bips: number): string {
  if (!Number.isSafeInteger(bips) || bips < 0) {
    throw new RangeError(`bips must be a whole number of basis points, got ${bips}`)
  }
  const whole = Math.trunc(bips / 100)
  const hundredths = bips % 100
  if (hundredths === 0) return String(whole)
  if (hundredths % 10 === 0) return `${whole}.${hundredths / 10}`
  return `${whole}.${String(hundredths).padStart(2, '0')}`
}

/** Positive is money coming towards you; negative is money going away. */
export function directionOf(amountCents: number): Direction {
  if (amountCents > 0) return 'incoming'
  if (amountCents < 0) return 'outgoing'
  return 'settled'
}

/**
 * A balance between you and one other person, as a sentence.
 *
 * `amountCents` is that person's row on `GET /api/balances`: positive means
 * they owe you. The label is what a screen reader reads out and what the eye
 * lands on; `amount` is the digits beside it.
 */
export function describePerson(
  displayName: string,
  amountCents: number,
  currency: string,
): { direction: Direction; cents: number; label: string; amount: string; sentence: string } {
  const direction = directionOf(amountCents)
  const amount = formatMoney(amountCents, currency)

  const label =
    direction === 'incoming' ? 'owes you' : direction === 'outgoing' ? 'you owe' : 'settled up'

  const sentence =
    direction === 'incoming'
      ? `${displayName} owes you ${amount}`
      : direction === 'outgoing'
        ? `You owe ${displayName} ${amount}`
        : `You and ${displayName} are settled up`

  return { direction, cents: amountCents, label, amount, sentence }
}

/**
 * A row of `GET /api/groups/:id/balances`, where positive means *the group*
 * owes that person. Your own row is written in the second person, because
 * "Ada is owed 75.01" on Ada's own screen reads as somebody else.
 */
export function describeMember(
  displayName: string,
  amountCents: number,
  currency: string,
  isYou: boolean,
): { direction: Direction; cents: number; label: string; amount: string; sentence: string } {
  const direction = directionOf(amountCents)
  const amount = formatMoney(amountCents, currency)

  const label = isYou
    ? { incoming: 'you are owed', outgoing: 'you owe', settled: 'settled up' }[direction]
    : { incoming: 'is owed', outgoing: 'owes', settled: 'settled up' }[direction]

  const who = isYou ? 'You' : displayName
  const sentence =
    direction === 'settled'
      ? `${who} ${isYou ? 'are' : 'is'} settled up`
      : isYou
        ? `${who} ${direction === 'incoming' ? 'are owed' : 'owe'} ${amount}`
        : `${who} ${direction === 'incoming' ? 'is owed' : 'owes'} ${amount}`

  return { direction, cents: amountCents, label, amount, sentence }
}

/**
 * The home screen's headline: what you are owed less what you owe, said in
 * words before it is said in digits.
 */
export function describeNet(
  netCents: number,
  currency: string,
): { direction: Direction; cents: number; headline: string; amount: string } {
  const direction = directionOf(netCents)
  return {
    direction,
    cents: netCents,
    headline:
      direction === 'incoming'
        ? 'you are owed, overall'
        : direction === 'outgoing'
          ? 'you owe, overall'
          : 'you are all settled up',
    amount: formatMoney(netCents, currency),
  }
}

/**
 * `2026-08-14` → `August 2026`. The month headings the expense list is
 * grouped under.
 *
 * The date is split rather than parsed: `new Date('2026-08-14')` is UTC
 * midnight, which in any timezone west of London is the 13th, and a list
 * grouped by month would put the first of a month under the one before it for
 * half the world.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function monthKeyOf(isoDate: string): string {
  return isoDate.slice(0, 7)
}

export function formatMonth(monthKey: string): string {
  const [year, month] = monthKey.split('-')
  const name = MONTHS[Number(month) - 1]
  return name === undefined ? monthKey : `${name} ${year}`
}

/** `2026-08-14` → `14 Aug`, for a row inside a month heading that already says the year. */
export function formatDay(isoDate: string): string {
  const [, month, day] = isoDate.split('-')
  const name = MONTHS[Number(month) - 1]
  return name === undefined ? isoDate : `${Number(day)} ${name.slice(0, 3)}`
}

/**
 * `2027-03-05` → `5 Mar 2027`.
 *
 * `formatDay` with the year said as well, for a date with no month heading
 * over it to say it instead — a schedule's next occurrence, most of all:
 * "next on 5 Jan", read in December, is exactly the sentence that needs one.
 */
export function formatDated(isoDate: string): string {
  const day = formatDay(isoDate)
  return day === isoDate ? isoDate : `${day} ${isoDate.slice(0, 4)}`
}

/** Initials for the member stack — one letter, or two for a two-word name. */
export function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase()
  return (words[0].slice(0, 1) + words[words.length - 1].slice(0, 1)).toUpperCase()
}

/**
 * Splits a list already in newest-first order into month sections, keeping
 * that order. It groups rather than sorts: the server has already put the
 * rows in the order they belong in, and re-sorting here would be a second
 * opinion about an order that is the database's to hold.
 */
export function groupByMonth<T>(
  rows: readonly T[],
  dateOf: (row: T) => string,
): { monthKey: string; heading: string; rows: T[] }[] {
  const sections: { monthKey: string; heading: string; rows: T[] }[] = []
  for (const row of rows) {
    const monthKey = monthKeyOf(dateOf(row))
    const last = sections[sections.length - 1]
    if (last && last.monthKey === monthKey) last.rows.push(row)
    else sections.push({ monthKey, heading: formatMonth(monthKey), rows: [row] })
  }
  return sections
}

/** What one transaction row says about *your* stake in it, in words. */
export function describeYourStake(
  myId: UserId,
  paid: readonly { userId: UserId; amountCents: number }[],
  owed: readonly { userId: UserId; amountCents: number }[],
  currency: string,
): { direction: Direction; cents: number; label: string; amount: string } {
  return describeStake(myId, paid, owed, currency, true)
}

/**
 * The same reading for anybody on the transaction, not only you — what the
 * expense sheet's settlement preview writes against each person while the
 * split is still being edited, so that the words it previews are the words
 * the group's list will say once it is filed.
 *
 * Your own row is in the second person ("you lent"); anybody else's is the
 * bare verb ("lent", "borrowed"), because it sits beside their name. Somebody
 * who put in exactly their own share is *square* rather than "not involved":
 * they were in it, and it moved nothing for them.
 */
export function describeStake(
  userId: UserId,
  paid: readonly { userId: UserId; amountCents: number }[],
  owed: readonly { userId: UserId; amountCents: number }[],
  currency: string,
  isYou: boolean,
): { direction: Direction; cents: number; label: string; amount: string } {
  const theirs = (rows: readonly { userId: UserId; amountCents: number }[]) =>
    rows.find((row) => row.userId === userId)?.amountCents ?? 0

  const net = theirs(paid) - theirs(owed)
  const direction = directionOf(net)
  const involved = theirs(paid) !== 0 || theirs(owed) !== 0

  const words = isYou
    ? { incoming: 'you lent', outgoing: 'you borrowed', square: 'you are square', out: 'not involved' }
    : { incoming: 'lent', outgoing: 'borrowed', square: 'square', out: 'not involved' }

  return {
    direction,
    cents: net,
    label:
      direction === 'incoming'
        ? words.incoming
        : direction === 'outgoing'
          ? words.outgoing
          : involved
            ? words.square
            : words.out,
    amount: formatMoney(net, currency),
  }
}

/**
 * What part of a bill an amount is, as a percentage: `5000` of `10000` is
 * `50%`. Exact to a hundredth of a percent when it divides that finely, and
 * said to be approximate when it does not — a third of $100.00 is 33.34% of it
 * on the person who takes the odd cent, and "about 33%" is honest where
 * "33.33%" would be a figure the cents do not add up to.
 */
export function formatShareOf(partCents: number, wholeCents: number): string {
  if (!Number.isSafeInteger(partCents) || !Number.isSafeInteger(wholeCents) || wholeCents <= 0) {
    throw new RangeError(`need a whole part of a positive whole, got ${partCents} of ${wholeCents}`)
  }
  const scaled = Math.abs(partCents) * 10000
  if (scaled % wholeCents === 0) return `${percentInput(scaled / wholeCents)}%`
  return `about ${Math.round((Math.abs(partCents) * 100) / wholeCents)}%`
}

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/**
 * A number of bytes, as a person reads it.
 *
 * Binary multiples — 1 KB is 1024 bytes — because that is what the plan limits
 * are written in (`512 * 1024 * 1024`), and a bar that says "512 MB" over a
 * limit stored as 536,870,912 has to mean the same MB in both halves or the
 * bar is a slightly wrong picture of a number the server is enforcing exactly.
 *
 * One decimal place from KB up, none for bytes, because "1.0 B" is not a thing
 * anybody says.
 */
export function formatBytes(bytes: number): string {
  const value = Math.max(0, Math.round(bytes))
  if (value < 1024) return `${value} B`

  let scaled = value
  let unit = 0
  while (scaled >= 1024 && unit < UNITS.length - 1) {
    scaled /= 1024
    unit += 1
  }
  // One decimal, but not a trailing `.0`: 1.5 GB, and 2 GB rather than 2.0 GB.
  const rounded = Math.round(scaled * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${UNITS[unit]}`
}

/**
 * The sentence under the quota bar.
 *
 * It is written from the reading rather than from raw numbers so that the bar
 * and the words cannot disagree, for the same reason an `Amount` colours and
 * labels from one `direction`. Unlimited says so plainly instead of drawing an
 * empty bar with no explanation, and an overflow says how much over it is —
 * "63 KB too much" is something a person can act on, where "storage full" is
 * not.
 */
export function describeQuota(reading: {
  usedBytes: number
  limitBytes: number | null
  addingBytes: number
  overBytes: number
  fits: boolean
}): string {
  if (reading.limitBytes === null) {
    return reading.addingBytes > 0
      ? `${formatBytes(reading.usedBytes)} stored, and no limit on this account.`
      : `${formatBytes(reading.usedBytes)} stored. No limit on this account.`
  }

  const of = `of ${formatBytes(reading.limitBytes)}`
  if (reading.addingBytes === 0) {
    return `${formatBytes(reading.usedBytes)} ${of} used.`
  }
  if (!reading.fits) {
    return (
      `${formatBytes(reading.usedBytes)} ${of} used — this would need ` +
      `${formatBytes(reading.overBytes)} more room than you have.`
    )
  }
  return (
    `${formatBytes(reading.usedBytes)} ${of} used; this adds ` +
    `${formatBytes(reading.addingBytes)}.`
  )
}
